import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as groupsApi from '@/api/groups';
import type { GroupFilters, GroupFormData, AddGroupMemberData, GroupMemberRole } from '@/types/group';

const GROUPS_KEY = ['groups'] as const;

export function useGroups(filters?: GroupFilters) {
  return useQuery({
    queryKey: [...GROUPS_KEY, 'list', filters],
    queryFn: () => groupsApi.getGroups(filters),
  });
}

export function useGroup(id: string | undefined) {
  return useQuery({
    queryKey: [...GROUPS_KEY, 'detail', id],
    queryFn: () => groupsApi.getGroup(id!),
    enabled: !!id,
  });
}

export function useGroupMembers(id: string | undefined) {
  return useQuery({
    queryKey: [...GROUPS_KEY, 'members', id],
    queryFn: () => groupsApi.getGroupMembers(id!),
    enabled: !!id,
  });
}

export function useCreateGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: GroupFormData) => groupsApi.createGroup(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...GROUPS_KEY, 'list'] });
    },
  });
}

export function useUpdateGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<GroupFormData> }) =>
      groupsApi.updateGroup(id, data),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: [...GROUPS_KEY, 'list'] });
      void queryClient.invalidateQueries({ queryKey: [...GROUPS_KEY, 'detail', variables.id] });
    },
  });
}

export function useDeleteGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => groupsApi.deleteGroup(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...GROUPS_KEY, 'list'] });
    },
  });
}

export function useAddGroupMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      groupId,
      data,
    }: {
      groupId: string;
      data: AddGroupMemberData;
    }) => groupsApi.addGroupMember(groupId, data),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: [...GROUPS_KEY, 'members', variables.groupId],
      });
      void queryClient.invalidateQueries({
        queryKey: [...GROUPS_KEY, 'detail', variables.groupId],
      });
      void queryClient.invalidateQueries({ queryKey: ['members', 'detail'] });
    },
  });
}

export function useRemoveGroupMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      groupId,
      memberId,
    }: {
      groupId: string;
      memberId: string;
    }) => groupsApi.removeGroupMember(groupId, memberId),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: [...GROUPS_KEY, 'members', variables.groupId],
      });
      void queryClient.invalidateQueries({
        queryKey: [...GROUPS_KEY, 'detail', variables.groupId],
      });
      void queryClient.invalidateQueries({ queryKey: ['members', 'detail'] });
    },
  });
}

export function useUpdateGroupMemberRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      groupId,
      memberId,
      role,
    }: {
      groupId: string;
      memberId: string;
      role: GroupMemberRole;
    }) => groupsApi.updateGroupMemberRole(groupId, memberId, role),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: [...GROUPS_KEY, 'members', variables.groupId],
      });
      void queryClient.invalidateQueries({
        queryKey: [...GROUPS_KEY, 'detail', variables.groupId],
      });
      void queryClient.invalidateQueries({ queryKey: ['members', 'detail'] });
    },
  });
}

export function useAddGroupMembersBatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      groupId,
      members,
    }: {
      groupId: string;
      members: Array<{ member_id: string; role?: string; section_id?: string | null }>;
    }) => groupsApi.addGroupMembersBatch(groupId, members),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: [...GROUPS_KEY, 'members', variables.groupId],
      });
      void queryClient.invalidateQueries({
        queryKey: [...GROUPS_KEY, 'detail', variables.groupId],
      });
      void queryClient.invalidateQueries({ queryKey: [...GROUPS_KEY, 'list'] });
    },
  });
}

export function useUpdateGroupMemberSection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      groupId,
      memberId,
      sectionId,
    }: {
      groupId: string;
      memberId: string;
      sectionId: string | null;
    }) => groupsApi.updateGroupMemberSection(groupId, memberId, sectionId),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: [...GROUPS_KEY, 'members', variables.groupId],
      });
      void queryClient.invalidateQueries({
        queryKey: [...GROUPS_KEY, 'detail', variables.groupId],
      });
    },
  });
}

// ── Sections (admin-managed) ───────────────────────────────────────────

function invalidateGroupSectionQueries(queryClient: ReturnType<typeof useQueryClient>, groupId: string): void {
  void queryClient.invalidateQueries({ queryKey: [...GROUPS_KEY, 'detail', groupId] });
  void queryClient.invalidateQueries({ queryKey: [...GROUPS_KEY, 'members', groupId] });
  // Also invalidate meeting cache — meetings embed group.sections in their payload.
  void queryClient.invalidateQueries({ queryKey: ['attendance'] });
}

export function useCreateGroupSection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, name, color }: { groupId: string; name: string; color?: string | null }) =>
      groupsApi.createGroupSection(groupId, { name, color }),
    onSuccess: (_data, v) => invalidateGroupSectionQueries(queryClient, v.groupId),
  });
}

export function useUpdateGroupSection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sectionId, name, color }: { sectionId: string; groupId: string; name?: string; color?: string | null }) =>
      groupsApi.updateGroupSection(sectionId, { name, color }),
    onSuccess: (_data, v) => invalidateGroupSectionQueries(queryClient, v.groupId),
  });
}

export function useDeleteGroupSection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sectionId }: { sectionId: string; groupId: string }) =>
      groupsApi.deleteGroupSection(sectionId),
    onSuccess: (_data, v) => invalidateGroupSectionQueries(queryClient, v.groupId),
  });
}

export function useReorderGroupSections() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, sectionIds }: { groupId: string; sectionIds: string[] }) =>
      groupsApi.reorderGroupSections(groupId, sectionIds),
    onSuccess: (_data, v) => invalidateGroupSectionQueries(queryClient, v.groupId),
  });
}
